import { useEffect, useMemo, useState } from 'react';
import useThreeInteractionStore from '@/modules/three/hooks/useThreeInteractionStore';
import { IconButton } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SculptOpts } from '@/thirdPartyLibraries/three-mesh-halfedge/src/operations/sculpt';

type Num = number | undefined;

const clamp01 = (x: Num) => Math.min(1, Math.max(0, Number.isFinite(x as number) ? (x as number) : 0));
const clampPct = (x: Num) => Math.min(100, Math.max(0, Number.isFinite(x as number) ? (x as number) : 0));

/** Defaults – keep optional fields actually optional (no hard-plane unless enabled). */
const DEFAULTS: SculptOpts = {
  area: 0,              // 0 = use rings
  rings: 1,
  mode: 'project',
  strength: 1,
  falloff: 'smooth',
  sigma: 0.5,
  boundaryPolicy: 'slide',
  normalKind: 'face',
  percentile: 50,
  percentileBias: [50, 10], // lerp(hi, lo, strength) → at strength=1 tends to 10 (fill)
  clamp: {
    enabled: false,
    minQuality: 0.15,
    maxIterations: 10,
  },
  // hard-plane extras (kept undefined by default)
  // direction: undefined,
  // planeOffset: undefined,
  // planePoint: undefined,
};

function NumberField({
  label,
  value,
  min,
  max,
  step = 0.1,
  onChange,
  disabled,
  suffix,
}: {
  label: string;
  value: number;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  suffix?: string;
  onChange: (n: number) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-3 text-sm my-1">
      <span className="opacity-80">{label}</span>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          value={Number.isFinite(value) ? value : 0}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          className="h-8 w-28"
        />
        {suffix && <span className="opacity-60 text-xs">{suffix}</span>}
      </div>
    </label>
  );
}

function RangeField({
  label,
  value,
  min = 0,
  max = 1,
  step = 0.01,
  onChange,
  disabled,
  suffix,
}: {
  label: string;
  value: number;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  suffix?: string;
  onChange: (n: number) => void;
}) {
  const decs = step < 1 ? ((`${step}`.split('.')[1]?.length ?? 0)) : 0;
  return (
    <div className="my-1">
      <div className="flex justify-between text-sm">
        <span className="opacity-80">{label}</span>
        <span className="opacity-60">
          {Number.isFinite(value) ? value.toFixed(decs) : '0'}{suffix ?? ''}
        </span>
      </div>
      <input
        type="range"
        className="w-full"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(parseFloat(e.target.value))}
      />
    </div>
  );
}

export function SculptTool() {
  const { setSculptMode, sculptMode } = useThreeInteractionStore();

  const initial = useMemo(() => {
    const s: SculptOpts = { ...DEFAULTS, ...(sculptMode ?? {}) };
    s.clamp = { ...DEFAULTS.clamp, ...(s.clamp ?? {}) };
    s.percentileBias = (s.percentileBias ?? DEFAULTS.percentileBias) as [number, number];
    return s;
  }, [sculptMode]);

  const [form, setForm] = useState<SculptOpts>(initial);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // push to store whenever form changes
  useEffect(() => {
    setSculptMode(form);
  }, [form, setSculptMode]);

  const set = <K extends keyof SculptOpts>(key: K, val: SculptOpts[K]) =>
    setForm((f) => ({ ...f, [key]: val }));

  const useRadius = (form.area ?? 0) > 0;
  const hardPlaneEnabled = form.planeOffset !== undefined; // our simple toggle

  return (
    <div className="bg-content p-4 text-white flex flex-col gap-3 rounded-2xl">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Sculpt</h2>
        <div className="flex gap-2">
          <IconButton
            variant="secondary"
            onClick={() => setForm({ ...DEFAULTS })}
          >
            Reset
          </IconButton>
          <IconButton onClick={() => setShowAdvanced(a => !a)}>
            {showAdvanced ? 'Hide advanced' : 'Show advanced'}
          </IconButton>
        </div>
      </div>

      {/* Mode */}
      <div className="grid grid-cols-2 gap-2">
        <label className="text-sm opacity-80">Mode</label>
        <select
          className="bg-transparent border rounded-md px-2 py-1"
          value={form.mode}
          onChange={(e) => set('mode', e.target.value as any)}
        >
          <option value="project">Project (Flatten/Fill/Scrape)</option>
          <option value="relax">Relax (Smooth)</option>
        </select>

        {/* Brush size */}
        <label className="text-sm opacity-80">Brush size</label>
        <div className="flex gap-2">
          <NumberField
            label="Radius"
            value={form.area ?? 0}
            min={0}
            step={0.01}
            suffix="world"
            onChange={(n) => set('area', Math.max(0, n))}
          />
          <NumberField
            label="Rings"
            value={form.rings ?? 1}
            min={1}
            step={1}
            onChange={(n) => set('rings', Math.max(1, Math.round(n)))}
          />
        </div>

        {/* Falloff */}
        <label className="text-sm opacity-80">Falloff</label>
        <div className="flex items-center gap-2">
          <select
            className="bg-transparent border rounded-md px-2 py-1"
            value={form.falloff}
            onChange={(e) => set('falloff', e.target.value as any)}
          >
            <option value="smooth">Smooth</option>
            <option value="gauss">Gauss</option>
            <option value="linear">Linear</option>
            <option value="none">None</option>
          </select>
          <NumberField
            label="Sigma"
            value={form.sigma ?? 0.5}
            min={0.001}
            step={0.05}
            disabled={form.falloff !== 'gauss'}
            onChange={(n) => set('sigma', Math.max(0.001, n))}
          />
        </div>

        {/* Strength */}
        <label className="text-sm opacity-80">Strength</label>
        <RangeField
          label=""
          value={form.strength ?? 1}
          min={0}
          max={1}
          step={0.01}
          onChange={(n) => set('strength', clamp01(n))}
        />
      </div>

      {/* Project-mode specifics */}
      {form.mode === 'project' && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-sm opacity-80">Percentile</label>
            <div className="flex items-center gap-2">
              <RangeField
                label=""
                value={form.percentile ?? 50}
                min={0}
                max={100}
                step={1}
                onChange={(n) => set('percentile', clampPct(n))}
              />
              <span className="text-xs opacity-60 w-10 text-right">{(form.percentile ?? 50).toFixed(0)}%</span>
            </div>

            <label className="text-sm opacity-80">Boundary</label>
            <select
              className="bg-transparent border rounded-md px-2 py-1"
              value={form.boundaryPolicy}
              onChange={(e) => set('boundaryPolicy', e.target.value as any)}
            >
              <option value="slide">Slide</option>
              <option value="flatten">Flatten</option>
              <option value="lock">Lock</option>
            </select>

            <label className="text-sm opacity-80">Normal</label>
            <select
              className="bg-transparent border rounded-md px-2 py-1"
              value={form.normalKind}
              onChange={(e) => set('normalKind', e.target.value as any)}
            >
              <option value="face">Face-weighted</option>
              <option value="pca">PCA</option>
            </select>

            {/* Plane offset (HARD PLANE) */}
            <div className="col-span-2 mt-1 rounded-lg border border-white/10 p-2">
              <div className="flex items-center justify-between">
                <label className="text-sm opacity-80">Hard plane</label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={hardPlaneEnabled}
                    onChange={(e) => {
                      if (e.target.checked) {
                        // enable with 0 as starting offset (n·x = 0); you’ll typically also set 'direction' elsewhere
                        set('planeOffset', 0);
                      } else {
                        // disable → undefined, sculpt ignores plane mode
                        set('planeOffset', undefined);
                      }
                    }}
                  />
                  <span>Enable</span>
                </label>
              </div>

              <div className="mt-2 grid grid-cols-2 gap-2">
                <NumberField
                  label="Plane offset (d)"
                  value={form.planeOffset ?? 0}
                  step={0.01}
                  disabled={!hardPlaneEnabled}
                  onChange={(n) => set('planeOffset', n)}
                  suffix="n·x = d"
                />
              </div>

              <p className="text-xs opacity-60 mt-1">
                Hard plane projects vertices onto the plane <code>n·x = d</code>. You must also set a{" "}
                <b>direction</b> (plane normal) in your tool logic (e.g., camera view). Disable to return to percentile-based flatten.
              </p>
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            <IconButton
              variant="secondary"
              onClick={() => setForm((f) => ({ ...f, mode: 'project', percentile: 50 }))}
            >
              Preset: Flatten (50%)
            </IconButton>
            <IconButton
              variant="secondary"
              onClick={() => setForm((f) => ({ ...f, mode: 'project', percentile: 10 }))}
            >
              Preset: Fill (10%)
            </IconButton>
            <IconButton
              variant="secondary"
              onClick={() => setForm((f) => ({ ...f, mode: 'project', percentile: 90 }))}
            >
              Preset: Scrape (90%)
            </IconButton>
          </div>
        </>
      )}

      {/* Relax-mode hint */}
      {form.mode === 'relax' && (
        <p className="text-xs opacity-70">
          Relax uses <span className="opacity-100">Strength</span> as step size each stroke. Increase{" "}
          <span className="opacity-100">iterations</span> in your tool logic for stronger smoothing.
        </p>
      )}

      {/* Advanced */}
      {showAdvanced && (
        <div className="mt-2 rounded-xl border border-white/10 p-3">
          <h3 className="text-sm font-medium mb-2 opacity-90">Advanced</h3>

          {/* Percentile bias mapping */}
          <div className="grid grid-cols-2 gap-2">
            <label className="text-sm opacity-80">Percentile bias (lo → hi)</label>
            <div className="flex items-center gap-2">
              <NumberField
                label="Low"
                value={(form.percentileBias?.[1] ?? 10)}
                min={0}
                max={100}
                step={1}
                onChange={(n) =>
                  set('percentileBias', [form.percentileBias?.[0] ?? 50, clampPct(n)] as [number, number])
                }
              />
              <NumberField
                label="High"
                value={(form.percentileBias?.[0] ?? 50)}
                min={0}
                max={100}
                step={1}
                onChange={(n) =>
                  set('percentileBias', [clampPct(n), form.percentileBias?.[1] ?? 10] as [number, number])
                }
              />
            </div>

            {/* Clamp */}
            <label className="text-sm opacity-80">Quality clamp</label>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!form.clamp?.enabled}
                  onChange={(e) => set('clamp', { ...(form.clamp ?? {}), enabled: e.target.checked })}
                />
                <span>Enabled</span>
              </label>
              <NumberField
                label="Min quality"
                value={form.clamp?.minQuality ?? 0.15}
                min={0}
                max={1}
                step={0.01}
                disabled={!form.clamp?.enabled}
                onChange={(n) => set('clamp', { ...(form.clamp ?? {}), minQuality: clamp01(n) })}
              />
              <NumberField
                label="Backtrack"
                value={form.clamp?.maxIterations ?? 10}
                min={1}
                step={1}
                disabled={!form.clamp?.enabled}
                onChange={(n) => set('clamp', { ...(form.clamp ?? {}), maxIterations: Math.max(1, Math.round(n)) })}
              />
            </div>
          </div>

          <p className="text-xs opacity-60 mt-2">
            Tip: if <b>Area</b> is 0, the brush uses <b>Rings</b>. On uneven meshes, prefer a world-space <b>Area</b>.
          </p>
        </div>
      )}
    </div>
  );
}